// The decisions behind the Send invoice and Mark paid modals, kept pure so they can be tested (TD-073, TD-077).
import { httpStatusOf } from "@/core/http/HttpError";

/** Two years back at most, as the server refuses (payment_rules.PAST_DAYS_MAX): a typo'd year never books. */
export const PAST_DAYS_MAX = 730;

/** The ISO date `days` before `iso`, in UTC so a daylight-saving change never shifts it. */
export const daysBefore = (iso: string, days: number): string => {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day - days)).toISOString().slice(0, 10);
};

export function dateError(date: string, today: string, what: string): string | undefined {
  if (!date) return `Enter the ${what} date`;
  if (date > today) return `The ${what} date cannot be in the future`;
  return date < daysBefore(today, PAST_DAYS_MAX) ? `The ${what} date is more than two years ago` : undefined;
}

/** A query as a paired modal reads it: its data once loaded, and its last error. */
export interface Loadable {
  data: unknown;
  error: unknown;
}

/** loading, ready, missing (the subject is gone: a 404) or failed (worth a Retry). */
export type JobLoad = "loading" | "ready" | "missing" | "failed";

/**
 * A modal reads two queries on every entry path: the subject it is about (a job, a person) and a
 * second one that names what it shows (the work type catalog). Ready needs both, missing is a 404 on the
 * subject, failed is either one failing with no data.
 */
export function pairLoad(subject: Loadable, support: Loadable): JobLoad {
  if (httpStatusOf(subject.error) === 404) return "missing";
  if (subject.data !== undefined && support.data !== undefined) return "ready";
  const failed = (q: Loadable) => q.data === undefined && !!q.error;
  return failed(subject) || failed(support) ? "failed" : "loading";
}

/** What a failed pairLoad says beside Retry: the subject's message when it failed, else the support query's. */
export const pairFailure = (subject: Loadable, subjectMessage: string, supportMessage: string): string =>
  subject.data === undefined && subject.error ? subjectMessage : supportMessage;

/** Retry a failed job read twice, but never a 404: a missing job stays missing. */
export const retryUnlessMissing = (failureCount: number, error: unknown): boolean => httpStatusOf(error) !== 404 && failureCount < 2;
